import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { fetchMyProfile } from '../../api/auth';
import {
  fetchCompanyInventory,
  fetchCompanyInventoryMasters,
} from '../../api/companyInventory';
import {
  createMidInspection,
  deleteMidInspectionAttachment,
  fetchMidInspection,
  fetchMidInspections,
  fetchNextMidLotNo,
  updateMidInspection,
  uploadMidInspectionAttachment,
} from '../../api/midInspection';
import {
  CompanyInventoryItem,
  CompanyInventoryMaster,
} from '../../types/companyInventory';
import { INSPECTION_FORM_TYPE_MID } from '../../types/inspectionForm';
import {
  defaultStaticForm,
  listStaticForms,
  StaticInspectionForm,
} from '../../data/staticInspectionForms';
import {
  EMPTY_MID_ITEM,
  MidInspection,
  MidInspectionAttachment,
  MidInspectionInput,
  MidInspectionItem,
  MidInspectionPayload,
  midInspectionItemLabel,
  midInspectionStatusLabel,
  validateMidInputStock,
} from '../../types/midInspection';
import { MID_PROCESS_REQUIREMENT } from './midFormDefaults';
import CompanyInventoryPickerModal from './CompanyInventoryPickerModal';
import MidInspectionSheet, { MidSheetValues } from './MidInspectionSheet';
import PreviousInspectionPickerModal from './PreviousInspectionPickerModal';
import ProductionDailyLotPicker from './ProductionDailyLotPicker';
import {notifyError, reportError} from '../../utils/notify';
import './AcceptanceInspectionCreate.css';
import './ProductionDailyReportPanel.css';

interface Props {
  inspectionId?: number;
  contractId?: number | null;
  onCancel: () => void;
  onSaved: (inspectionId: number) => void;
}

const initialSheet: MidSheetValues = {
  inventoryId: null,
  itemKind: '',
  itemName: '',
  specText: '',
  authorName: '',
  lotNo: '',
  lotQty: '',
  inspectorName: '',
  inspectionDate: new Date().toISOString().slice(0, 10),
  appearanceRemark: '',
  appearanceJudgment: '',
  electricalRemark: '',
  electricalJudgment: '',
  agingRemark: '',
  agingJudgment: '',
  overallJudgment: '',
  remark: '',
  items: [],
  inputs: [],
};

function MidInspectionCreate({
  inspectionId,
  contractId = null,
  onCancel,
  onSaved,
}: Props) {
  const isEdit = inspectionId != null;
  const forms = useMemo(() => listStaticForms(INSPECTION_FORM_TYPE_MID), []);
  const [inventory, setInventory] = useState<CompanyInventoryItem[]>([]);
  const [masters, setMasters] = useState<CompanyInventoryMaster[]>([]);
  const [formKey, setFormKey] = useState<string | null>(() => defaultStaticForm(INSPECTION_FORM_TYPE_MID).key);
  const [sheet, setSheet] = useState<MidSheetValues>(initialSheet);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lotNoHint, setLotNoHint] = useState(
    '저장 시 자동 발급됩니다. (예: 2026-MID-1)'
  );
  const [previousRows, setPreviousRows] = useState<MidInspection[]>([]);
  const [showLoadPanel, setShowLoadPanel] = useState(false);
  const [loadingPrevious, setLoadingPrevious] = useState(false);
  const [pendingRemarkFiles, setPendingRemarkFiles] = useState<File[]>([]);
  const [savedAttachments, setSavedAttachments] = useState<MidInspectionAttachment[]>([]);
  const [lotPickerIndex, setLotPickerIndex] = useState<number | null>(null);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const [inputPickerIndex, setInputPickerIndex] = useState<number | null>(null);
  const authorNameRef = useRef('');

  const modelSummary = sheet.itemName
    ? `[완제품] ${sheet.itemName}${sheet.specText ? ` · ${sheet.specText}` : ''}`
    : '';

  const selectedForm: StaticInspectionForm | null =
    forms.find((f) => f.key === formKey) ?? null;

  const selectForm = (key: string) => {
    setFormKey(key);
    if (!isEdit) {
      const form = forms.find((f) => f.key === key);
      setSheet((prev) => ({
        ...prev,
        items:
          form && form.items.length > 0
            ? form.items.map((item) => ({
                ...EMPTY_MID_ITEM,
                processName: MID_PROCESS_REQUIREMENT,
                itemName: item.itemName ?? '',
                standardText: item.standardText ?? '',
                methodText: item.methodText ?? '',
              }))
            : [],
      }));
    }
  };

  const mapDetailToSheet = (detail: MidInspection, authorFallback: string): MidSheetValues => ({
    inventoryId: detail.inventoryId ?? null,
    itemKind: detail.itemKind ?? '',
    itemName: detail.itemName ?? '',
    specText: detail.specText ?? '',
    authorName: detail.authorName || authorFallback,
    lotNo: detail.lotNo ?? '',
    lotQty: detail.lotQty ?? '',
    inspectorName: detail.inspectorName ?? authorFallback,
    inspectionDate: detail.inspectionDate?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
    appearanceRemark: detail.appearanceRemark ?? '',
    appearanceJudgment: detail.appearanceJudgment ?? '',
    electricalRemark: detail.electricalRemark ?? '',
    electricalJudgment: detail.electricalJudgment ?? '',
    agingRemark: detail.agingRemark ?? '',
    agingJudgment: detail.agingJudgment ?? '',
    overallJudgment: detail.overallJudgment ?? '',
    remark: detail.remark ?? '',
    items:
      (detail.items ?? []).length > 0
        ? (detail.items ?? []).map((item) => ({
            processName: MID_PROCESS_REQUIREMENT,
            itemName: item.itemName ?? '',
            standardText: item.standardText ?? '',
            methodText: item.methodText ?? '',
            sample1: item.sample1 ?? '',
            sample2: item.sample2 ?? '',
            sample3: item.sample3 ?? '',
            sample4: item.sample4 ?? '',
            sample5: item.sample5 ?? '',
            judgment: item.judgment ?? '',
          }))
        : [],
    inputs:
      (detail.inputs ?? [])
        .filter((row) => row.inventoryId)
        .map((row) => ({
          acceptanceInspectionId: row.acceptanceInspectionId ?? null,
          inventoryId: row.inventoryId,
          itemKind: row.itemKind ?? '',
          itemName: row.itemName ?? '',
          specText: row.specText ?? '',
          lotNo: row.lotNo ?? '',
          qty: row.qty ?? '',
          stockQty: row.stockQty ?? null,
        })),
  });

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        const [inventoryRows, masterRows, history, lotSuggestion] = await Promise.all([
          fetchCompanyInventory(),
          fetchCompanyInventoryMasters(),
          fetchMidInspections(),
          isEdit ? Promise.resolve(null) : fetchNextMidLotNo().catch(() => null),
        ]);
        setInventory(inventoryRows);
        setMasters(masterRows);
        setPreviousRows(history);
        if (!isEdit && lotSuggestion) {
          const suggested = lotSuggestion.suggestedValue?.trim() || '';
          setLotNoHint(
            suggested
              ? `저장 시 자동 발급됩니다. (예: ${suggested})`
              : '저장 시 자동 발급됩니다. (예: 2026-MID-1)'
          );
        }

        if (isEdit && inspectionId != null) {
          const detail = await fetchMidInspection(inspectionId);
          if (detail.status === 'APPROVED') {
            notifyError('승인 완료된 중간검사는 수정할 수 없습니다.');
            return;
          }
          const matched =
            forms.find((f) => f.title === (detail.formTitle ?? '')) ?? forms[0] ?? null;
          if (matched) setFormKey(matched.key);
          setSheet(mapDetailToSheet(detail, authorNameRef.current));
          setSavedAttachments(detail.attachments ?? []);
          setPendingRemarkFiles([]);
        } else if (forms[0]) {
          setFormKey(forms[0].key);
        }
        setError('');
      } catch {
        reportError(setError, isEdit
            ? '중간검사 수정 정보를 불러오지 못했습니다.'
            : '중간검사 등록 정보를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    })();
    void fetchMyProfile()
      .then((profile) => {
        const name = profile.nickname?.trim() || profile.loginId?.trim() || '';
        authorNameRef.current = name;
        setSheet((prev) => ({
          ...prev,
          authorName: prev.authorName || name,
          inspectorName: prev.inspectorName || name,
        }));
      })
      .catch(() => undefined);
  }, [inspectionId, isEdit, forms]);

  const applyPrevious = async (rowId: number) => {
    try {
      setLoadingPrevious(true);
      setError('');
      const detail = await fetchMidInspection(rowId);
      const matched =
        forms.find((f) => f.title === (detail.formTitle ?? '')) ?? forms[0] ?? null;
      if (matched) setFormKey(matched.key);
      setSheet((prev) => ({
        ...mapDetailToSheet(detail, authorNameRef.current || prev.authorName),
        authorName: isEdit
          ? detail.authorName || prev.authorName
          : authorNameRef.current || prev.authorName,
        lotNo: isEdit ? detail.lotNo ?? '' : '',
        inspectionDate: new Date().toISOString().slice(0, 10),
      }));
      setPendingRemarkFiles([]);
      if (!isEdit) setSavedAttachments([]);
      setShowLoadPanel(false);
    } catch {
      notifyError('이전 중간검사를 불러오지 못했습니다.');
    } finally {
      setLoadingPrevious(false);
    }
  };

  const applyModel = (item: CompanyInventoryItem) => {
    if (item.itemKind !== 'FINISHED') {
      notifyError('완제품만 선택할 수 있습니다.');
      return;
    }
    setSheet((prev) => ({
      ...prev,
      inventoryId: item.inventoryId,
      itemKind: item.itemKind,
      itemName: item.itemName,
      specText: item.specText ?? '',
    }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!sheet.inventoryId) {
      notifyError('결과 모델(완제품)을 선택해 주세요.');
      return;
    }
    if (!selectedForm) {
      notifyError('중간검사 양식을 선택해 주세요.');
      return;
    }
    if (sheet.inputs.length === 0) {
      notifyError('투입 원자재를 1개 이상 추가해 주세요.');
      return;
    }
    if (sheet.inputs.some((row) => !row.inventoryId)) {
      notifyError('투입 품목을 모두 선택해 주세요.');
      return;
    }
    if (!sheet.lotQty.trim()) {
      notifyError('생산 LOT 수량은 필수입니다.');
      return;
    }
    if (!sheet.overallJudgment.trim()) {
      notifyError('종합판정은 필수입니다.');
      return;
    }
    const stockMap = new Map<number, number>();
    for (const item of inventory) {
      stockMap.set(item.inventoryId, Number(item.stockQty ?? 0));
    }
    for (const input of sheet.inputs) {
      if (!stockMap.has(input.inventoryId) && input.stockQty != null) {
        stockMap.set(input.inventoryId, Number(input.stockQty));
      }
    }
    const stockError = validateMidInputStock(sheet.inputs, stockMap);
    if (stockError) {
      notifyError(stockError);
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload: MidInspectionPayload = {
        inventoryId: sheet.inventoryId,
        contractId: contractId ?? undefined,
        formTitle: selectedForm.title,
        status: 'COMPLETED',
        authorName: sheet.authorName || undefined,
        lotQty: sheet.lotQty.trim(),
        inspectorName: sheet.inspectorName || undefined,
        inspectionDate: sheet.inspectionDate || null,
        appearanceRemark: sheet.appearanceRemark || undefined,
        appearanceJudgment: sheet.appearanceJudgment || undefined,
        electricalRemark: sheet.electricalRemark || undefined,
        electricalJudgment: sheet.electricalJudgment || undefined,
        agingRemark: sheet.agingRemark || undefined,
        agingJudgment: sheet.agingJudgment || undefined,
        overallJudgment: sheet.overallJudgment.trim(),
        remark: sheet.remark || undefined,
        items: sheet.items.map((item) => ({
          ...item,
          processName: MID_PROCESS_REQUIREMENT,
        })),
        inputs: sheet.inputs,
      };
      if (isEdit && inspectionId != null) {
        const updated = await updateMidInspection(inspectionId, payload);
        for (const file of pendingRemarkFiles) {
          await uploadMidInspectionAttachment(updated.inspectionId, file);
        }
        onSaved(updated.inspectionId);
      } else {
        const created = await createMidInspection(payload);
        for (const file of pendingRemarkFiles) {
          await uploadMidInspectionAttachment(created.inspectionId, file);
        }
        window.alert(`Lot No. ${created.lotNo || '-'}로 등록되었습니다.`);
        onSaved(created.inspectionId);
      }
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          (isEdit ? '중간검사 수정에 실패했습니다.' : '중간검사 등록에 실패했습니다.')
      );
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (index: number, patch: Partial<MidInspectionItem>) =>
    setSheet((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));

  const addItem = () => {
    setSheet((prev) => ({
      ...prev,
      items: [...prev.items, { ...EMPTY_MID_ITEM, processName: MID_PROCESS_REQUIREMENT }],
    }));
  };

  const removeItem = (index: number) => {
    setSheet((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const addInput = () => {
    setSheet((prev) => ({
      ...prev,
      inputs: [
        ...prev.inputs,
        {
          acceptanceInspectionId: null,
          inventoryId: 0,
          itemKind: '',
          itemName: '',
          specText: '',
          lotNo: '',
          qty: '',
          stockQty: null,
        },
      ],
    }));
  };

  const applyInputInventory = (index: number, item: CompanyInventoryItem) => {
    updateInput(index, {
      inventoryId: item.inventoryId,
      itemKind: item.itemKind,
      itemName: item.itemName,
      specText: item.specText ?? '',
      lotNo: '',
      acceptanceInspectionId: null,
      stockQty: item.stockQty ?? null,
    });
  };

  const updateInput = (index: number, patch: Partial<MidInspectionInput>) =>
    setSheet((prev) => ({
      ...prev,
      inputs: prev.inputs.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));

  const removeInput = (index: number) => {
    setSheet((prev) => ({
      ...prev,
      inputs: prev.inputs.filter((_, i) => i !== index),
    }));
    setLotPickerIndex((prev) => {
      if (prev == null) return prev;
      if (prev === index) return null;
      return prev > index ? prev - 1 : prev;
    });
  };

  return (
    <div className="aic">
      <div className="aic__header">
        <div>
          <h3 className="aic__title">{isEdit ? '중간검사 수정' : '중간검사 등록'}</h3>
          <p className="aic__subtitle">
            결과 모델(완제품)을 고르고, 재고관리 품목에서 투입 원자재를 선택한 뒤 검사 항목을
            작성하세요. Lot은 선택 사항이며, 승인 시 보유 재고가 차감됩니다.
            {isEdit
              ? ' 다른 사람이 작성한 성적서를 수정하면 검토란에 본인 이름이 기록됩니다.'
              : ''}
          </p>
        </div>
        <div className="aic__actions">
          {!isEdit ? (
            <button
              type="button"
              className="aic__btn is-ghost"
              disabled={previousRows.length === 0}
              onClick={() => setShowLoadPanel(true)}
            >
              이전 작성 불러오기
            </button>
          ) : null}
          <button type="button" className="aic__btn is-ghost" onClick={onCancel}>
            취소
          </button>
        </div>
      </div>

      {error ? <p className="aic__error">{error}</p> : null}
      {loading ? <p className="aic__empty">불러오는 중...</p> : null}

      <PreviousInspectionPickerModal
        open={showLoadPanel}
        title="이전 중간검사 선택"
        emptyText="이전 중간검사가 없습니다."
        loading={loadingPrevious}
        columns={[
          { key: 'item', label: '모델' },
          { key: 'form', label: '양식' },
          { key: 'lot', label: 'Lot' },
          { key: 'date', label: '검사일' },
          { key: 'status', label: '상태' },
        ]}
        rows={previousRows.map((row) => ({
          id: row.inspectionId,
          cells: {
            item: midInspectionItemLabel(row),
            form: row.formTitle || '-',
            lot: row.lotNo || '-',
            date: row.inspectionDate?.slice(0, 10) || '-',
            status: midInspectionStatusLabel(row.status),
          },
        }))}
        onClose={() => setShowLoadPanel(false)}
        onSelect={applyPrevious}
      />

      <section className="aic__card">
        <div className="aic__card-head">
          <h4 className="aic__step">
            <span className="aic__step-num">1</span>
            결과 모델(완제품) 선택
          </h4>
          <p className="aic__hint">검색 후 선택합니다. 없으면 팝업에서 빠른 등록할 수 있습니다.</p>
        </div>
        <div className="aic__form-grid">
          <label className="aic__field">
            모델
            <button
              type="button"
              className={`pdr-pick-btn${modelSummary ? ' has-value' : ''}`}
              onClick={() => setModelPickerOpen(true)}
            >
              {modelSummary || '모델 선택'}
            </button>
          </label>
          <label className="aic__field">
            규격
            <input value={sheet.specText} readOnly placeholder="모델 선택 시 자동" />
          </label>
        </div>
      </section>

      <CompanyInventoryPickerModal
        open={modelPickerOpen}
        items={inventory.filter((row) => row.itemKind === 'FINISHED')}
        masters={masters.filter((row) => row.itemKind === 'FINISHED')}
        initialKind="FINISHED"
        title="결과 모델(완제품) 선택"
        onClose={() => setModelPickerOpen(false)}
        onListsUpdated={(nextInventory, nextMasters) => {
          setInventory(nextInventory);
          setMasters(nextMasters);
        }}
        onSelect={applyModel}
      />

      <CompanyInventoryPickerModal
        open={inputPickerIndex != null}
        items={inventory.filter(
          (row) => row.itemKind === 'MATERIAL' || row.itemKind === 'PART'
        )}
        masters={masters.filter(
          (row) => row.itemKind === 'MATERIAL' || row.itemKind === 'PART'
        )}
        initialKind="MATERIAL"
        title="투입 품목 선택"
        onClose={() => setInputPickerIndex(null)}
        onListsUpdated={(nextInventory, nextMasters) => {
          setInventory(nextInventory);
          setMasters(nextMasters);
        }}
        onSelect={(item) => {
          if (inputPickerIndex == null) return;
          if (item.itemKind !== 'MATERIAL' && item.itemKind !== 'PART') {
            notifyError('원자재 또는 부품만 투입할 수 있습니다.');
            return;
          }
          applyInputInventory(inputPickerIndex, item);
        }}
      />

      <section className="aic__card">
        <div className="aic__card-head">
          <h4 className="aic__step">
            <span className="aic__step-num">2</span>
            중간검사 양식
          </h4>
          <p className="aic__hint">화면 고정 양식입니다. 나중에 템플릿을 추가하면 여기서 고를 수 있습니다.</p>
        </div>
        {forms.length === 0 ? (
          <p className="aic__empty">중간검사 양식이 없습니다.</p>
        ) : (
          <table className="aic__form-table">
            <thead>
              <tr>
                <th style={{ width: 48 }} />
                <th>양식명</th>
              </tr>
            </thead>
            <tbody>
              {forms.map((form) => (
                <tr
                  key={form.key}
                  className={formKey === form.key ? 'is-selected' : undefined}
                  onClick={() => selectForm(form.key)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <input
                      className="aic__radio"
                      type="radio"
                      checked={formKey === form.key}
                      onChange={() => selectForm(form.key)}
                    />
                  </td>
                  <td>{form.title}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {formKey ? (
        <form onSubmit={(e) => void submit(e)}>
          <div className="aic__footer">
            <button type="button" className="aic__btn is-ghost" onClick={onCancel}>
              취소
            </button>
            <button type="submit" className="aic__btn is-primary" disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
          <section className="aic__card">
            <div className="aic__card-head">
              <h4 className="aic__step">
                <span className="aic__step-num">3</span>
                성적서 입력
              </h4>
              <p className="aic__hint">
                투입은 재고 품목 선택 · Lot은 선택 · 검사항목은 직접 추가
              </p>
            </div>
            <MidInspectionSheet
              lotNoHint={lotNoHint}
              editable
              values={sheet}
              inventoryOptions={inventory}
              remarkAttachments={savedAttachments}
              pendingRemarkFiles={pendingRemarkFiles}
              onChange={(patch) => setSheet((prev) => ({ ...prev, ...patch }))}
              onItemChange={updateItem}
              onAddItem={addItem}
              onRemoveItem={removeItem}
              onAddInput={addInput}
              onInputChange={updateInput}
              onRemoveInput={removeInput}
              onOpenModelPicker={() => setModelPickerOpen(true)}
              onOpenInputInventoryPicker={(index) => setInputPickerIndex(index)}
              onOpenInputLotPicker={(index) => setLotPickerIndex(index)}
              onPickRemarkFiles={(files) => setPendingRemarkFiles((prev) => [...prev, ...files])}
              onRemovePendingRemarkFile={(index) =>
                setPendingRemarkFiles((prev) => prev.filter((_, i) => i !== index))
              }
              onDeleteAttachment={(file) => {
                if (!isEdit || inspectionId == null) return;
                if (!window.confirm(`"${file.fileName}" 첨부를 삭제할까요?`)) return;
                void deleteMidInspectionAttachment(inspectionId, file.attachmentId)
                  .then(() =>
                    setSavedAttachments((prev) =>
                      prev.filter((row) => row.attachmentId !== file.attachmentId)
                    )
                  )
                  .catch(() => reportError(setError, '첨부파일 삭제에 실패했습니다.'));
              }}
            />
            {lotPickerIndex != null && sheet.inputs[lotPickerIndex]?.inventoryId ? (
              <ProductionDailyLotPicker
                inventory={
                  inventory.find(
                    (row) => row.inventoryId === sheet.inputs[lotPickerIndex].inventoryId
                  ) || {
                    inventoryId: sheet.inputs[lotPickerIndex].inventoryId,
                    itemKind: sheet.inputs[lotPickerIndex].itemKind || 'PART',
                    itemName: sheet.inputs[lotPickerIndex].itemName || '품목',
                    specText: sheet.inputs[lotPickerIndex].specText || '',
                    stockQty: sheet.inputs[lotPickerIndex].stockQty ?? 0,
                  }
                }
                onClose={() => setLotPickerIndex(null)}
                onSelect={(pick) => {
                  updateInput(lotPickerIndex, {
                    acceptanceInspectionId: pick.acceptanceInspectionId,
                    inventoryId: pick.inventoryId,
                    itemName: pick.itemName || sheet.inputs[lotPickerIndex].itemName,
                    specText: pick.specText || sheet.inputs[lotPickerIndex].specText,
                    lotNo: pick.lotNo,
                    // 투입수량은 자동 채우지 않음 (오투입 방지)
                  });
                  setLotPickerIndex(null);
                }}
              />
            ) : null}
            <div className="aic__footer">
              <button type="button" className="aic__btn is-ghost" onClick={onCancel}>
                취소
              </button>
              <button type="submit" className="aic__btn is-primary" disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </section>
        </form>
      ) : null}
    </div>
  );
}

export default MidInspectionCreate;
